import { Form, Formik } from 'formik';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { COMMAND_STATE, ENROLL_TYPE, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../../../constants/command-status';
import { useLocale } from '../../../contexts/localeContext';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useIcaWebsocketContext } from '../../../contexts/websocket/IcaWebsocketContext';
import { EnrolmentDevice } from '../../../models/EnrolmentDevices';
import { EnrolmentTask } from '../../../models/EnrolmentTask';
import { ExecuteCommand } from '../../../models/ExecuteCommand';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { Name } from '../../../models/Name';
import { Tag } from '../../../models/Tag';
import WizardSideBarStep from '../../../models/WizardSideBarStep';
import { getBiometricNamesWithPagination } from '../../../services/biometricService';
import { saveCredential } from '../../../services/credentialsService';
import { enrolFace } from '../../../services/executeCommandService';
import { getErrorMessage } from '../../../services/utilities/ServiceError';
import { IPluginCameraImage } from '../../../services/wsPluginService/plugins/base/cameraModels';
import enrollCmd from '../../../services/wsPluginService/plugins/base/enrollCmd';
import { IShowEnrollValidOptions } from '../../../services/wsPluginService/plugins/base/pluginModels';
import { decodeCameraPluginMsg } from '../../../services/wsPluginService/plugins/cameraDecoder';
import { getEnrollResultCommandType } from '../../../services/wsPluginService/plugins/decodePluginMsg';
import { buildCardData, generateNewRandomPAC } from '../../../utilities/CredentialUtil';
import { appendFilter } from '../../../utilities/filters';
import FullScreenActivityFrame from '../../activity-frame/full-screen-activity-spinner';
import { useDialog } from '../../dialog/DialogContext';
import Wizard from '../../wizard/wizard';
import ChooseCard from '../choose-card';
import DetailsForm from '../details-form';
import SelectDeviceForm from '../select-device-form';
import SubTasksForm from '../sub-tasks-form';
import CameraCaptureForm from './camera-capture-form';
import SummaryForm from './summary-form';

interface Props {
    availablePlugins: IShowEnrollValidOptions;
    availableSystemDeviceIds: number[],
    cardInfo: Tag;
    onClose: () => void;
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    onBack: (event: any) => void;
    updateBadge: (badge: number) => void;
    pluginWSInstance: WebSocket | undefined;
    hasTags: boolean;
    masterId: number | undefined;
}

const EnrolFaceWizard: FC<Props> = ({ availablePlugins, cardInfo, onClose, defaultContent, defaultSteps, onBack, updateBadge, availableSystemDeviceIds, pluginWSInstance, hasTags, masterId = 0 }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const [device, setDevice] = useState<Name>();
    const [listParameters, setListParameters] = useState<ListQuery>();
    const [deviceType, setDeviceType] = useState('');
    const [busyScanning, setBusyScanning] = useState<boolean>(false);
    const enrolmentDevice = new EnrolmentDevice(translate);
    const enrolmentTask = new EnrolmentTask(translate);
    const [image, setImage] = useState<any>('');
    const [isControlIdFaceImage, setControlIdFaceImage] = useState<any>(false);
    const [isHikCameraImage, setHikCameraImage] = useState<any>(false);
    const [isHikFaceImage, setHikFaceImage] = useState<any>(false);
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();
    const [newCard, setNewCard] = useState<Tag>(cardInfo);
    const [isNewCard, setIsNewCard] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<MultiSelectOption>({ value: "", label: "" });

    const onMessage = (response: any) => {
        const jsonData = JSON.parse(response.data);
        if (jsonData.command === MONITORING_COMMAND.MONITORING_ENROL && jsonData.attributes.subCommand === ENROLL_TYPE.ENROLL_IDEMIA_FINGER) {
            if (jsonData.attributes.cmdState === COMMAND_STATE.COMMAND_COMPLETE) {
                toast.success(translate.byKey(TranslationKeys.credentialSaved));
                updateBadge(1);
            }
            else {
                toast.warn(jsonData.attributes.errMessage);
            }
            onClose();
            setBusyScanning(false);
        }
    }

    const onMessagePluginInstance = (response: any) => {
        if (getEnrollResultCommandType(response) === enrollCmd.CMD_CAMERA) {
            const cameraImage = decodeCameraPluginMsg(response) as IPluginCameraImage;
            setBusyScanning(false);
            setImage(cameraImage.image);
        }
    }

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    const onFirstLocalNext = (event) => {
        if (event.task === enrolmentDevice.CONTROLID_CAMERA.value) {
            setDeviceType(event.description);
            setControlIdFaceImage(true);
            setBusyScanning(true);

            if (pluginWSInstance && pluginWSInstance?.readyState === WebSocket.OPEN) {
                pluginWSInstance?.send("{'enrollType':'camera','type':'capture'}");
                pluginWSInstance.onmessage = (value) => {
                    onMessagePluginInstance(value.data)
                };
            }
        }
        else if (event.task === enrolmentDevice.HIKVISION_CAMERA.value) {
            setDeviceType(event.description);
            setHikCameraImage(true);
            setBusyScanning(true);

            if (pluginWSInstance && pluginWSInstance?.readyState === WebSocket.OPEN) {
                pluginWSInstance?.send("{'enrollType':'camera','type':'capture'}");
                pluginWSInstance.onmessage = (value) => {
                    onMessagePluginInstance(value.data)
                };
            }
        }
        else if(event.task === enrolmentDevice.HIK_VISION_FACE_1.value){
            setHikFaceImage(true);
            const allFaceDevices = [...enrolmentDevice.ENROL_FACE_DEVICES];
            const selectedDeviceId = allFaceDevices.find(item => item.label === event.description)?.id.toString();
            setListParameters({ ...listParameters, filters: appendFilter(selectedDeviceId, 'Device Type', 'bioType.id', []) });
        }
        // if none of the above
        else if (event.task && event.task != enrolmentDevice.CONTROLID_CAMERA.value) {
            setDeviceType(event.description);
            const allFaceDevices = [...enrolmentDevice.ENROL_FACE_PLUGIN, ...enrolmentDevice.ENROL_FACE_DEVICES];
            const selectedDeviceId = allFaceDevices.find(item => item.label === event.description)?.id.toString();
            setListParameters({ ...listParameters, filters: appendFilter(selectedDeviceId, 'Device Type', 'bioType.id', []) });
        }

        setStep(step + 1);
    }

    const onLocalNext = (event) => {
        if (event.code) {
            setSelectedTag({ ...selectedTag, label: event.code });
        }
        if (isControlIdFaceImage) {
            const devicesToTestWith = [enrolmentDevice.CONTROLID_IDFACE.id, enrolmentDevice.CONTROLID_IDFACEMAX.id, enrolmentDevice.HID_AMICO.id];
            setListParameters({ ...listParameters, filters: appendFilter(devicesToTestWith, 'Device Type', 'bioType.id', []) });
        }
        if (isHikCameraImage) {
            const devicesToTestWith = [enrolmentDevice.HIK_VISION_FACE_1.id];
            setListParameters({ ...listParameters, filters: appendFilter(devicesToTestWith, 'Device Type', 'bioType.id', []) });
        }
        
        else if (event.task) {
            const allFaceDevices = [...enrolmentDevice.ENROL_FACE_PLUGIN, ...enrolmentDevice.ENROL_FACE_DEVICES];
            const selectedDeviceId = allFaceDevices.find(item => item.label === event.description)?.id.toString();
            setListParameters({ ...listParameters, filters: appendFilter(selectedDeviceId, 'Device Type', 'bioType.id', []) });
        }
        setStep(step + 1);
    }

    const onLocalBack = (event: any) => {
        if (step === 1) {
            onBack(event);
            return;
        } else if (step === 2) {
            setDeviceType("")
        };

        setStep(step - 1);
    }

    const onLocalSave = async (values) => {
        // Determine the initial enrollment type
        const commandSubType = getCommandSubType(image, isHikFaceImage, isHikCameraImage);
        try {
            setBusyScanning(true)

            //Create new tag if needed
            let newTag: any;
            if (isNewCard) {
                newTag = await saveCredential({ ...buildCardData({ ...values, masterId: masterId }, translate) });
            }

            //Build template information and save
            const command: ExecuteCommand = {
                cmd: ENROLL_TYPE.ENROLL,
                cmdSubType: commandSubType,
                tagId: newTag ? newTag.content.data.id : selectedTag.value,
                deviceId: device?.value,
                image: image
            }

            const response = await enrolFace(command);
            // Face captured via webcam. We have already saved the template so no need to wait for a websocket message.
            if (response.success == true && (commandSubType === ENROLL_TYPE.ENROLL_CONTROLID_FACE || commandSubType === ENROLL_TYPE.ENROLL_HIKVISION_FACE)) {
                // There was an issue after testing the controlId face
                if (response.data['cmd-status'] == COMMAND_STATE.COMMAND_FAILED) {
                   toast.warn(translate.byKey(response.data['cmd']));
                }
                else {
                    toast.success(translate.byKey(TranslationKeys.credentialSaved));
                }
                updateBadge(1);
            }

            // For all other enrolments we need to let the websocket be in charge
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            if (commandSubType === ENROLL_TYPE.ENROLL_CONTROLID_FACE) {
                onClose();
                setBusyScanning(false);
            }
        }
    }

    function getCommandSubType(image, isHikFaceImage, isHikCameraImage) {
        let commandSubType;

        if (isHikCameraImage || isHikFaceImage) {
            commandSubType = ENROLL_TYPE.ENROLL_HIKVISION_FACE;
        } else if (image) {
            commandSubType = ENROLL_TYPE.ENROLL_CONTROLID_FACE;
        } else {
            commandSubType = ENROLL_TYPE.ENROLL_IDEMIA_FACE;
        }

        return commandSubType;
    }

    const onChooseCardNext = (e) => {
        if (e.value && e.value === enrolmentDevice.MANUAL_CODE_TYPE.value) {
            setNewCard({
                ...e.values,
                code: cardInfo.code,
                typeId: cardInfo.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: '',
            });
            setIsNewCard(true);
        } else if (e.value && e.value === enrolmentDevice.RANDOM_PAC.value) {
            const tag: Tag = generateNewRandomPAC(newCard, translate)
            setNewCard({
                ...e.values,
                code: tag.code,
                tagDisplay: tag.code,
                type: tag.type,
                typeId: tag.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: '',
            });
            setSelectedTag({ ...selectedTag, label: tag.code })
            setIsNewCard(true);
        } else {
            setSelectedTag({ value: e.value, label: e.code })
            setStep(step + 1);
        }
    }

    const getExtraContent = () => {
        if (isControlIdFaceImage || isHikCameraImage) {
            return [
                <CameraCaptureForm
                    image={image}
                    onBack={(event) => onLocalBack(event)}
                    onNext={(event) => onLocalNext(event)}
                />,
                <SelectDeviceForm
                    dataMethod={getBiometricNamesWithPagination}
                    onNext={(event) => onLocalNext(event)}
                    onBack={(event) => onLocalBack(event)}
                    setSelectedDevice={setDevice}
                    listParameters={listParameters} />
            ]
        }

        return [<SelectDeviceForm
            dataMethod={getBiometricNamesWithPagination}
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSelectedDevice={setDevice}
            listParameters={listParameters} />];
    }

    const getExtraStep = (): WizardSideBarStep[] => {
        if (isControlIdFaceImage || isHikCameraImage) {
            return [
                {
                    title: translate.byKey(TranslationKeys.selectDevice),
                    description: device?.label ?? '',
                    substeps: []
                },
                {
                    title: translate.byKey(TranslationKeys.card),
                    description: selectedTag.label,
                    substeps: []
                }
            ]
        }

        return [{
            title: translate.byKey(TranslationKeys.card),
            description: selectedTag.label,
            substeps: []
        }];
    }

    const content = [
        ...defaultContent,
        <SubTasksForm
            onNext={(event) => onFirstLocalNext(event)}
            pluginsAvailable={availablePlugins}
            onBack={(event) => onLocalBack(event)}
            enrollMethod={enrolmentTask.ENROL_FACE.value}
            availableSystemDeviceIds={availableSystemDeviceIds} />,
        ...getExtraContent(),
        isNewCard ?
            <DetailsForm
                onBack={(e) => { setIsNewCard(false); }}
                onNext={onLocalNext}
                cardInfo={newCard} /> :
            <ChooseCard
                masterId={masterId}
                onBack={onLocalBack}
                onNext={onChooseCardNext} />,
        <SummaryForm
            cardCode={selectedTag.label}
            deviceType={deviceType}
            device = {device?.label}
            onBack={(event) => onLocalBack(event)}
            taskDescription={enrolmentTask.ENROL_FACE.label} />,
    ]

    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.taskProcedure),
            description: deviceType,
            substeps: []
        },
        {
            title: isControlIdFaceImage || isHikCameraImage ? translate.byKey(TranslationKeys.imageCaptured) : translate.byKey(TranslationKeys.device),
            description: device?.label ?? '',
            substeps: []
        },
        ...getExtraStep(),
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    return <>
        {busyScanning ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.scanning)} /> : null}
        <Formik
            enableReinitialize={true}
            initialValues={cardInfo}
            onSubmit={onLocalSave}>
            <Form>
                <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                    step={step}
                    onClose={() => onClose()}
                    sidebarSteps={steps}>
                    {content}
                </Wizard>
            </Form>
        </Formik>
    </>
}

export default EnrolFaceWizard;
